/**
 * @file searchRedirect.ts
 *
 * @fileoverview Handles are redirect logic for filtering in search pages.
 *
 * When we select items from filters that will eventually call a redirect with
 * the new querystring object (and clean ulr part if applicable) with the
 * `redirectSearchWithFilter` function. Searches made with the fulltext search will
 * call the `redirectWithFullText` function.
 */

import Router from 'next/router';
import {
  addSearchUrlItem,
  removeSearchUrlItem,
  getSearchUrlItems,
  URLfilter,
  removeSearchUrlItemValue,
} from '~/jobiqo/services/searchUrlItems';
import { GeoInput } from '~/jobiqo/types/types';
import {
  getGeoLocationUrlObjectFromGeoInput,
  getLocationUrlKeys,
} from './searchHelpers';
import { ParsedUrlQuery } from 'querystring';
import { getTranslatedLink } from '~/jobiqo/utils/links';

/**
 * Cleans the querystring before redirecting.
 *
 * Removes certain items from the querystring that should not be
 * included in the redirect like pagination.
 *
 * @param {ParsedUrlQuery} query
 *   The query object from Next.js router.
 * @returns {ParsedUrlQuery}
 *   The cleaned query object.
 */
const getCleanQuerystringBeforeRedirect = (
  query: ParsedUrlQuery,
): ParsedUrlQuery => {
  const cleanedQuery: ParsedUrlQuery = {};

  // Transforms comma-delimited values into arrays
  // for better handling of filters.
  for (const key in query) {
    if (query.hasOwnProperty(key)) {
      let value = query[key];
      // Convert comma-delimited parts into arrays
      if (typeof value === 'string' && value.includes(',')) {
        const parts = value.split(',').map((item) => item.trim());
        // Only split into array if all parts are numeric (IDs).
        // Text filters like job_organization_name may contain commas in values.
        if (parts.every((part) => !isNaN(Number(part)))) {
          value = parts;
        }
      }
      // Add the cleaned value to the cleaned query object
      cleanedQuery[key] = value;
    }
  }

  const {
    jobId,
    params,
    page,
    path,
    router,
    taxonomy,
    ...restOfQueryString
  } = cleanedQuery;
  return restOfQueryString;
};

/**
 * Does a programmatic redirect when a non-link facet or filter change occurs.
 *
 * Prepares the querystring and sends a redirect so that the
 * query is re-fetched and the UI is updated.
 *
 * @param {string} searchUrl
 *  The search url to redirect to.
 *
 * @param {string} filter
 *  The filter to pass to the url.
 *
 * @param {number} cleanFacetsNumber
 *  The number of facets to show in the clean url section (e.g. /full-time/engineering).
 *
 * @param {any} cleanFacetsOrder
 *  The order of the facets in the clean url.
 *
 * @param {string[]} selectedItems
 *  The selected items for the filter passed.
 *
 * @param {string} alias
 *   An optional alias for the field being selected.
 */
export const redirectSearchWithFilter = (
  searchUrl: string,
  filter: string,
  cleanFacetsNumber,
  cleanFacetsOrder,
  selectedItems?: string[],
  alias?: string,
) => {
  // Get the current locale from the router.
  const locale = Router && Router.locale ? Router.locale : '';

  // Translate the search url if there is a route translation available.
  searchUrl = getTranslatedLink(searchUrl, locale);

  // The full querystring object (a Next.js object that holds all querystring items)
  // without unwanted items like pagination.
  let fullQuerystringWithFilters: any = getCleanQuerystringBeforeRedirect(
    Router.query,
  );

  // Gets the current clean url items in the url (e.g. /full-time)
  // This one gets re-calculated as the filters get applied
  // New items can be added and removed depending on the filter.
  let updatedItemsWithAlias = getSearchUrlItems();

  // If any of the selectedItems is not in the fullQuerystringWithFilters[filter] we are adding.
  // and not in the updatedItemsWithAlias
  const adding = selectedItems?.some((item) => {
    return (
      !updatedItemsWithAlias.some((aliasItem) => aliasItem.value === item) &&
      !fullQuerystringWithFilters?.[filter]?.includes(item.toString())
    );
  });

  // Get the current clean url (without the /jobs part)
  // e.g. /full-time/engineering.
  let currentAlias = Router.asPath
    .replace(`${searchUrl}/`, '')
    .replace(searchUrl, '');
  currentAlias = currentAlias.split('?')[0];

  // Check if there are properties for the filter being added or removed,
  // if so add, if not remove it accordingly.
  if (selectedItems && selectedItems.length > 0) {
    // we do have items being selected (selected items is not empty).
    // We simply add this to the querystring.
    fullQuerystringWithFilters = {
      ...fullQuerystringWithFilters,
      [filter]: selectedItems,
    };

    // If an item was previously in the url and was unchecked
    // we have to remove it.
    let shouldRemove = false;
    const aliasItemCurrentFilter = updatedItemsWithAlias.find(
      (element) => element.key === filter,
    );
    if (aliasItemCurrentFilter) {
      // Check if this item should be removed from the list of items in the
      // url (clean url items).
      shouldRemove =
        aliasItemCurrentFilter.key === filter &&
        !selectedItems?.includes(aliasItemCurrentFilter.value);

      if (shouldRemove) {
        // Remove this item from the tracking of current items in url
        // since this key / value was removed.
        updatedItemsWithAlias = removeSearchUrlItemValue(
          filter,
          aliasItemCurrentFilter.value,
        );
      }
    }

    // Only add if its an important key to track (see config for the search).
    if (alias && cleanFacetsOrder.includes(filter) && !shouldRemove && adding) {
      // We are adding things to the URL and nothing should be removed.
      const itemToAddAlias =
        updatedItemsWithAlias.length === 0 || adding
          ? alias
          : updatedItemsWithAlias.find((item) => item.key === filter)?.alias ??
            '';

      if (alias && updatedItemsWithAlias.length === 0) {
        // Add the last item in the selected items array.
        const newItemToAdd: URLfilter = {
          key: filter,
          value: selectedItems[0],
          alias,
        };
        updatedItemsWithAlias = addSearchUrlItem(newItemToAdd);
      } else if (itemToAddAlias) {
        // Add the last item in the selected items array.
        const newItemToAdd: URLfilter = {
          key: filter,
          value: adding
            ? selectedItems[selectedItems.length - 1]
            : selectedItems[0],
          alias: itemToAddAlias,
        };
        updatedItemsWithAlias = addSearchUrlItem(newItemToAdd);
      }
    }
  } else {
    // Remove the key entirely from the array since selected items is empty.
    const {
      [filter]: unusedProperty,
      ...withoutKey
    } = fullQuerystringWithFilters;
    fullQuerystringWithFilters = withoutKey;
    updatedItemsWithAlias = removeSearchUrlItem(filter);
  }

  // Now we calculate the new alias for the redirect.
  // This will depend on a number of things
  //
  // 1 ) Filters in the url
  // 2 ) cleanFacetsNumber (number of filters allowed in the clean url)
  // 3 ) cleanFacetsOrder (order and precedence filters have over each other)

  // The current items with their aliases.
  let nrItemsCurrentlyInCleanUrl: number = 0;
  let finalCleanUrlString = '';
  let finalCleanUrlItems: URLfilter[] = [];

  // Geo location is a special case, we don't want it to be a clean facet but
  // if its in the URL we should keep it there (should not be removed).
  [...cleanFacetsOrder, 'geo_location'].forEach((facet) => {
    // Adding items to the clean URL by order of the cleanUrlOrder.
    if (nrItemsCurrentlyInCleanUrl < cleanFacetsNumber) {
      const cleanUrlItem = updatedItemsWithAlias.find(
        (k) => k.key === facet && k.alias,
      );

      if (cleanUrlItem) {
        nrItemsCurrentlyInCleanUrl++;
        finalCleanUrlItems = [...finalCleanUrlItems, cleanUrlItem];
        finalCleanUrlString = finalCleanUrlString
          ? `${finalCleanUrlString}/${cleanUrlItem?.alias}`
          : `${cleanUrlItem?.alias}`;
      }
    } else {
      updatedItemsWithAlias.forEach((item) => {
        // If its not already in the full querystring we nee to add it.
        if (!fullQuerystringWithFilters[item.key]) {
          if (item.key === 'geo_location') {
            // Convert the string from item.value into an object (params)
            const locationParams = new URLSearchParams(
              `geo_location=${item.value}`,
            );
            const params = Object.fromEntries(locationParams.entries());

            // Merge the params object into fullQuerystringWithFilters
            fullQuerystringWithFilters = {
              ...fullQuerystringWithFilters,
              ...params,
            };
          } else {
            fullQuerystringWithFilters[item.key] = item.value;
          }
        }
      });

      // Remove items with the specified facet key from updatedItemsWithAlias
      updatedItemsWithAlias = updatedItemsWithAlias.filter(
        (item) => item.key !== facet,
      );
    }
  });

  // Calculate querystring without the items in clean url (everything that will be after ?)
  let queryStringToAddToUrl: string = '';
  queryStringToAddToUrl = findGetParametersForURL(
    fullQuerystringWithFilters,
    finalCleanUrlItems,
  );

  // Finally we perform the redirect.
  if (finalCleanUrlString) {
    // There is a clean url we need to use the router push with the
    // "as" property because of our clean URL.
    return Router.push(
      {
        pathname: Router.pathname,
        query: fullQuerystringWithFilters,
      },
      `${searchUrl}/${finalCleanUrlString}${
        queryStringToAddToUrl ? '?' + queryStringToAddToUrl : ''
      }`,
    );
  } else {
    // There is nothing in the clean URL (should not happen often, but can happen).
    // Perform the redirect. Use 'queryStringToAddToUrl' in the 'as' property
    // as it contains all the query parameters in the correct format (multiple
    // values joined by the comma).
    return Router.push(
      {
        pathname: searchUrl,
        query: fullQuerystringWithFilters,
      },
      `${searchUrl}${queryStringToAddToUrl ? '?' + queryStringToAddToUrl : ''}`,
    );
  }
};

/***
 * Calculates the items that should be appended to the URL as GET Parameters.
 *
 * The string querystring is basically only the first
 * item of all the faces that are in the clean url part. and if not
 * in the clean url part its all parts.
 *
 * @param {any} querystringObject
 *  The full querystring object.
 * @param {URLfilter[]} updatedItemsWithAlias
 *  The items that are currently in the clean url.
 *
 * @returns {string}
 *  The part of the URL that is used as a GET parameter.
 */
export const findGetParametersForURL = (
  querystringObject: any,
  updatedItemsWithAlias: URLfilter[],
): string => {
  // Collect all keys stored in clean url items.
  const updatedKeys = updatedItemsWithAlias.map((item) => item.key);
  // Collect all values stored in clean url items.
  // We always only want the first element of each key to be in a clean url.
  const updatedValues = updatedItemsWithAlias.map((item) => item.value);

  // Filter out values that are not in the clean url.
  const filterValues = (values: any[]) =>
    values.filter((val) => !updatedValues.includes(val));

  const processKey = (key: string) => {
    const value = querystringObject[key];

    // Special case for the term filter (skip).
    if (key === 'term') {
      return '';
    }

    // For text-based filters like job_organization_name, values may contain
    // commas, so we must use repeated keys (key=A&key=B) instead of
    // comma-separated (key=A,B). For now, we only check for job_organization_name
    // but we can extend this in the future.
    const useRepeatedKeys = key === 'job_organization_name';
    // Check if given key is in the clean url.
    const isInCleanUrl = updatedKeys.includes(key);

    // If the key is in the clean URL, filter out the clean-URL values.
    // Otherwise, use all values as-is.
    if (Array.isArray(value)) {
      const filtered = isInCleanUrl ? filterValues(value) : value;
      if (filtered.length === 0) {
        return '';
      }
      if (useRepeatedKeys) {
        return filtered.map((v) => `${key}=${encodeURIComponent(v)}`).join('&');
      }
      return `${key}=${filtered}`;
    }

    // Single value: skip if it's already represented in the clean URL.
    if (isInCleanUrl && updatedValues.includes(value)) {
      return '';
    }

    return `${key}=${useRepeatedKeys ? encodeURIComponent(value) : value}`;
  };

  return Object.keys(querystringObject)
    .map(processKey)
    .filter(Boolean)
    .join('&');
};

/**
 * Does the redirect to the job search without location or fulltext. will
 * preserve filters that are applied.
 */
export const redirectWithoutFulltextAndLocation = (searchUrl: string) => {
  // Get the current locale from the router.
  const locale = Router && Router.locale ? Router.locale : '';

  // Translate the search url if there is a route translation available.
  searchUrl = getTranslatedLink(searchUrl, locale);

  // Preserve any filters that are currently set.
  const querystringReturn: any = getCleanQuerystringBeforeRedirect(
    Router.query,
  );
  // Fine to mutate this here we just created it.
  delete querystringReturn.taxonomy;

  // Remove location and fulltext from the querystring.
  const urlParts = getLocationUrlKeys();
  urlParts.forEach((part) => delete querystringReturn[part]);
  const {
    search,
    geo_location,
    ...querystringWithoutLocationAndFulltext
  } = querystringReturn;

  Router.push({
    pathname: searchUrl,
    query: querystringWithoutLocationAndFulltext,
  });
};

/**
 * Does the redirect to the job search without location or fulltext. will
 * preserve filters that are applied.
 */
export const redirectWithFulltextAndOrLocation = (
  searchUrl: string,
  fulltext?: string,
  location?: GeoInput,
) => {
  // Get the current locale from the router.
  const locale = Router && Router.locale ? Router.locale : '';

  // Translate the search url if there is a route translation available.
  searchUrl = getTranslatedLink(searchUrl, locale);

  // The full querystring object (a Next.js object that holds all querystring items)
  // without unwanted items like pagination.
  let fullQuerystringWithFilters: any = getCleanQuerystringBeforeRedirect(
    Router.query,
  );

  // Gets the current clean url items in the url (e.g. /full-time)
  // This one gets re-calculated as the filters get applied
  // New items can be added and removed depending on the filter.
  let updatedItemsWithAlias = getSearchUrlItems();

  // Get the current clean url (without the /jobs part)
  // e.g. /full-time/engineering. We need to pass an empty string if path
  // does not contain the searchUrl to make sure search bar works
  // on the landing pages (so landing page alias is not passed as
  // the current alias).
  // Do this only if we are on the search page (could be multilingual)
  let currentAlias =
    Router.asPath.indexOf(`${searchUrl}/`) > -1
      ? Router.asPath.replace(`${searchUrl}/`, '').replace(searchUrl, '')
      : '';
  currentAlias = currentAlias.split('?')[0];

  // Append fulltext if set.
  // check if there are properties for the filter, if so add if not remove it.
  if (fulltext) {
    fullQuerystringWithFilters = {
      ...fullQuerystringWithFilters,
      search: fulltext,
    };
  } else {
    // remove the "search" from the array if there is no value for it.
    const {
      search: unusedProperty,
      ...querystringWithoutFulltext
    } = fullQuerystringWithFilters;
    fullQuerystringWithFilters = querystringWithoutFulltext;
  }

  // If the user is searching for a location an there is already one in
  // the clean URL we should remove the location from the clean URL and use
  // the user's selected location in the autocomplete.
  if (location?.label) {
    updatedItemsWithAlias.map((i) => {
      if (i.key === 'geo_location' && i?.alias) {
        currentAlias = currentAlias.replace(i?.alias, '');
      }
    });
    fullQuerystringWithFilters = {
      ...fullQuerystringWithFilters,
      ...getGeoLocationUrlObjectFromGeoInput(location),
    };
  } else {
    // remove all the geocomplete keys from the url.
    const urlParts = getLocationUrlKeys();
    urlParts.forEach((part) => delete fullQuerystringWithFilters[part]);
    const {
      geo_location,
      ...querystringWithoutLocation
    } = fullQuerystringWithFilters;
    fullQuerystringWithFilters = querystringWithoutLocation;
  }

  // Calculate querystring without the items in clean url (everything that will be after ?)
  let queryStringToAddToUrl: string = '';
  queryStringToAddToUrl = findGetParametersForURL(
    fullQuerystringWithFilters,
    updatedItemsWithAlias,
  );

  // Fine to mutate this here we just created it.
  delete fullQuerystringWithFilters.taxonomy;

  const searchPath = currentAlias ? `${searchUrl}/${currentAlias}` : searchUrl;

  if (updatedItemsWithAlias) {
    Router.push(
      {
        pathname: Router.pathname,
        query: fullQuerystringWithFilters,
      },
      `${searchPath}${
        queryStringToAddToUrl ? '?' + queryStringToAddToUrl : ''
      }`,
    );
  } else {
    Router.push({
      pathname: searchUrl,
      query: queryStringToAddToUrl,
    });
  }
};
